/**
 * AP-13 — FM-WIN-14: Lizenzfenster, Gegenstück zu `LicenseWindow.cpp`.
 *
 * Aufbau, Wortlaut und Taktung wie im Original: `License Key:` + Eingabefeld,
 * `Activate`/`Validate Now`, mehrzeilige Statusanzeige mit laufendem
 * Trial-Countdown im 1-s-Takt (FM-TIM-12), Schreiben nur bei Textänderung.
 * Beide Aktionen laufen asynchron, die Knöpfe sind währenddessen gesperrt.
 * Statt eines `MessageBoxA` erscheint der Fehler als Zeile im Fenster —
 * ein modaler Dialog ist im Browser nicht ohne Not nachbaubar.
 */

import './license.css';
import { OFFLINE_GRACE_DAYS, licenseManager } from '../license';
import { createButton, createInput, createLabel } from '../ui/controls';
import { createFloatingPanel } from './filters/FloatingPanel';
import type { CanConnection } from '../app/connection';
import type { AppStore } from '../app/store';

/** `PromptLicenseRequired()`, CanMonitorApp.cpp:3078-3087 (Wortlaut übernommen). */
const LOCKOUT_MESSAGE_ACTIVATED =
  'Your license is no longer valid (expired or the offline grace period has been exceeded). ' +
  'Please activate/validate your license to continue.';
const LOCKOUT_MESSAGE_TRIAL =
  'The unlicensed trial period has ended. Please activate a license to continue.';

interface LicenseViewHandle {
  show(): void;
  setNotice(text: string): void;
}

let instance: LicenseViewHandle | null = null;

function formatCountdown(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

/** Wie im Original: nur die letzten vier Zeichen bleiben lesbar. */
function maskKey(key: string): string {
  if (key === '') return '(none)';
  return key.length > 4 ? '*'.repeat(key.length - 4) + key.slice(-4) : key;
}

function createLicenseView(store: AppStore): LicenseViewHandle {
  const panel = createFloatingPanel('License');
  panel.element.classList.add('cm-panel-window--medium');

  const keyInput = createInput('XXXX-XXXX-XXXX-XXXX');
  keyInput.className += ' cm-license__key';
  const keyRow = document.createElement('div');
  keyRow.className = 'cm-panel-window__row';
  keyRow.append(createLabel('License Key:'), keyInput);

  const notice = document.createElement('div');
  notice.className = 'cm-license__notice';
  notice.hidden = true;

  const status = document.createElement('pre');
  status.className = 'cm-license__status';

  let busy = false;
  const activateButton = createButton('Activate', { accent: true, onClick: () => void run('activate') });
  const validateButton = createButton('Validate Now', { onClick: () => void run('validate') });

  const buttons = document.createElement('div');
  buttons.className = 'cm-panel-window__buttons';
  buttons.append(activateButton, validateButton);

  panel.body.append(keyRow, buttons, notice, status);

  async function run(action: 'activate' | 'validate'): Promise<void> {
    if (busy) return;
    const key = keyInput.value.trim();
    if (action === 'activate' && key === '') {
      setNotice('Enter a License Key first.');
      return;
    }

    busy = true;
    activateButton.disabled = true;
    validateButton.disabled = true;
    setNotice('');
    try {
      const error = action === 'activate' ? await licenseManager.activate(key) : await licenseManager.validate();
      if (error !== null) {
        setNotice(`${action === 'activate' ? 'Activation failed' : 'Validation failed'}: ${error}`);
      }
      store.emit('license:changed', {
        valid: licenseManager.isCurrentlyValid(),
        reason: error ?? licenseManager.statusText(),
      });
      store.emit('status:changed', {
        text: error === null
          ? `License ${action === 'activate' ? 'activated' : 'validated'}: ${licenseManager.statusText()}`
          : `License: ${error}`,
      });
    } finally {
      busy = false;
      refresh();
    }
  }

  function setNotice(text: string): void {
    notice.textContent = text;
    notice.hidden = text === '';
  }

  let lastStatusText = '';
  function refresh(): void {
    const state = licenseManager.state;
    const valid = licenseManager.isCurrentlyValid();

    // "Activate" bleibt nur hervorgehoben/aktiv, solange keine gültige Lizenz
    // besteht (LicenseWindow.cpp: WM_DRAWITEM + RefreshStatusText).
    activateButton.classList.toggle('cm-btn--accent', !valid);
    if (!busy) {
      activateButton.disabled = valid;
      validateButton.disabled = false;
    }

    let trialLine = '';
    if (!valid) {
      trialLine = licenseManager.isTrialActive()
        ? `Trial:               ${formatCountdown(licenseManager.trialSecondsRemaining())} remaining (unlicensed use)\n`
        : 'Trial:               expired -- activation required to continue\n';
    }

    const text =
      `Status:              ${licenseManager.statusText()}\n` +
      trialLine +
      `License Key:         ${maskKey(state.licenseKey)}\n` +
      `Installation ID:     ${state.installationId}\n` +
      `Expiry (server):     ${state.expiryUtc === '' ? '-' : state.expiryUtc}\n` +
      `Last Validated:      ${state.lastValidationUtc === '' ? '-' : state.lastValidationUtc}\n` +
      `Days Since Validate: ${licenseManager.daysSinceLastValidation()} (offline grace: ${OFFLINE_GRACE_DAYS} days)\n\n` +
      '"Activate" runs once against the license server using the\n' +
      'entered License Key. Afterward the app works offline and\n' +
      'periodically re-validates in the background; use\n' +
      '"Validate Now" to force an immediate check.';

    // Nur bei echter Änderung schreiben — sonst flackert der Block einmal je
    // Sekunde, obwohl sich (bei aktiver Lizenz) gar nichts bewegt.
    if (text === lastStatusText) return;
    lastStatusText = text;
    status.textContent = text;
  }

  // Der Countdown läuft nur, solange das Fenster sichtbar ist — im Original
  // startet/stoppt `ShowAndFocus()`/`WM_CLOSE` denselben 1-s-Timer.
  let timer: number | null = null;
  function stopTimer(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }
  panel.onHide(stopTimer);

  return {
    show(): void {
      keyInput.value = licenseManager.state.licenseKey;
      refresh();
      panel.show();
      stopTimer();
      timer = window.setInterval(refresh, 1000);
    },
    setNotice,
  };
}

/** `Help ⯆ > License...` (FM-MEN-36) — erzeugt das Fenster beim ersten Aufruf. */
export function showLicenseWindow(store: AppStore): void {
  instance ??= createLicenseView(store);
  instance.show();
}

/** `PromptLicenseRequired()` — Meldung, danach das Lizenzfenster (FM-LIC-01). */
export function promptLicenseRequired(store: AppStore): void {
  const message = licenseManager.state.activated ? LOCKOUT_MESSAGE_ACTIVATED : LOCKOUT_MESSAGE_TRIAL;
  store.emit('status:changed', { text: `License required: ${message}` });
  showLicenseWindow(store);
  instance?.setNotice(message);
}

/** `kLicenseRevalidateIntervalMs` (CanMonitorApp.cpp:165) — FM-TIM-05. */
const REVALIDATE_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Hängt den Lizenz-Client an die App: Startprüfung, Hintergrund-Revalidierung
 * alle 6 h und die Sperre, wenn Trial/Lizenz *während des Betriebs* abläuft
 * (CanMonitorApp.cpp:591-596 — dort trennt die App die Verbindung und meldet).
 */
export async function mountLicense(store: AppStore, connection: CanConnection): Promise<void> {
  await licenseManager.load();

  function publish(): void {
    store.emit('license:changed', {
      valid: licenseManager.isCurrentlyValid(),
      reason: licenseManager.statusText(),
    });
  }

  async function revalidate(): Promise<void> {
    if (!licenseManager.state.activated) return;
    await licenseManager.validate();
    publish();
  }

  publish();
  if (!licenseManager.isUsageAllowed()) promptLicenseRequired(store);
  void revalidate();
  window.setInterval(() => void revalidate(), REVALIDATE_INTERVAL_MS);

  let lockedOut = false;
  window.setInterval(() => {
    const allowed = licenseManager.isUsageAllowed();
    if (allowed) {
      lockedOut = false;
      return;
    }
    if (lockedOut) return;
    lockedOut = true;
    void connection.close();
    publish();
    promptLicenseRequired(store);
  }, 1000);
}
